// useVoiceQA — Jun 2026 (v3, smooth speech-to-speech).
//
// A hands-free voice conversation for the public Q&A. Tap the mic ONCE to
// start; you then just talk. The hook:
//   1. Listens and watches the mic level (Web Audio VAD). The mic is ONLY
//      active while listening — it goes quiet the moment the reply starts, so
//      background noise / the reply's own audio can never confuse it.
//   2. When you pause (~0.9s of quiet) it auto-sends your turn to
//      /voice/transcribe (Whisper). No second tap.
//   3. Runs the EXISTING grounded answer flow via `ask` (renders Q + A bubbles).
//   4. Speaks the answer back FAST — pipelined sentence-by-sentence and played
//      through ONE gesture-unlocked Web Audio context (gapless, and it works on
//      iOS where auto-played <audio> elements are blocked).
//   5. NO voice barge-in (by request). It reads the whole reply. To jump in,
//      tap the mic button again ("take over") — it stops the reply and listens.
//
// Billing: every transcribe + spoken chunk in one conversation shares a
// voice_session_id, so the backend rolls them into a SINGLE usage line.
//
// The voice layer NEVER bypasses the silo's grounded brain — speech only adds a
// microphone-in and a speaker-out around the same answer engine.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getVoiceTuning, startRouteHold, buildChunkSource } from "@/lib/voicePlayback";

// The 9 OpenAI TTS voices offered for a silo.
export const VOICE_OPTIONS = [
  { id: "alloy", label: "Alloy — balanced" },
  { id: "ash", label: "Ash — warm" },
  { id: "coral", label: "Coral — bright" },
  { id: "echo", label: "Echo — calm" },
  { id: "fable", label: "Fable — expressive" },
  { id: "nova", label: "Nova — friendly" },
  { id: "onyx", label: "Onyx — deep" },
  { id: "sage", label: "Sage — gentle" },
  { id: "shimmer", label: "Shimmer — soft" },
];

const SILENCE_MS = 1400; // quiet gap (after speech) that auto-sends the turn — raised from 900ms (Jun 2026, Randy) so a natural mid-question pause isn't mistaken for "I'm done"
const SPEECH_RMS = 0.018; // mic level that counts as "you're talking"
const MIN_SPEECH_MS = 350; // ignore blips shorter than this
const GUARD_CODES = ["LLM_GUARD", "SILO_VOUCHER_EMPTY", "OWNER_BYOK_NOT_SET"];

function genId() {
  try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* */ }
  return "v" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Strip light markdown so the spoken reply doesn't read "asterisk asterisk".
function speechClean(text) {
  return (text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_#>]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Break an answer into speakable chunks (sentence-ish, min length) so the first
// chunk can start playing almost immediately while the rest is fetched.
export function chunkForSpeech(text) {
  const clean = speechClean(text);
  if (!clean) return [];
  const parts = clean.match(/[^.!?]+[.!?]+|\S[^.!?]*$/g) || [clean];
  const out = [];
  let buf = "";
  for (const p of parts) {
    buf += (buf ? " " : "") + p.trim();
    // The FIRST chunk stays short (~180 chars) so speech starts fast; every
    // chunk after it is much larger (~600 chars). This turns a long answer
    // into a FEW big voice requests instead of 20+ tiny ones. Tiny requests
    // tripped OpenAI's per-minute limit, which forced 20–30s retry-backoff
    // pauses mid-answer (Randy's "random silence" bug). Fewer, larger requests
    // = no rate-limit stalls, and NO extra cost (TTS bills per character).
    // We only break at sentence ends (`parts` are whole sentences), so chunks
    // never cut a word in half. (Jun 2026, Randy)
    const minLen = out.length === 0 ? 180 : 600;
    if (buf.length >= minLen) {
      out.push(buf);
      buf = "";
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

// Smaller splitter for the FREE device voice (Web Speech API). Chrome cuts off
// any single utterance longer than ~15s, so we keep device chunks short
// (~180 chars ≈ ~10s) and speak them back-to-back. (Jun 2026, Randy — Option C)
function deviceChunks(text) {
  const clean = speechClean(text);
  if (!clean) return [];
  const parts = clean.match(/[^.!?]+[.!?]+|\S[^.!?]*$/g) || [clean];
  const out = [];
  let buf = "";
  for (const p of parts) {
    buf += (buf ? " " : "") + p.trim();
    if (buf.length >= 180) { out.push(buf); buf = ""; }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

// The browser's built-in (Web Speech) voices load asynchronously and are
// famously unreliable on Safari — getVoices() can return [] at first. We cache
// whatever we get and prefer a local English voice. (Jun 2026, Randy)
let _wsVoices = [];
function _refreshVoices() {
  try { const v = window.speechSynthesis?.getVoices?.(); if (v && v.length) _wsVoices = v; } catch { /* */ }
}
function _pickEnglishVoice() {
  if (!_wsVoices.length) _refreshVoices();
  return (
    _wsVoices.find((v) => /^en[-_]?/i.test(v.lang) && v.localService) ||
    _wsVoices.find((v) => /^en[-_]?/i.test(v.lang)) ||
    _wsVoices[0] ||
    null
  );
}

// Premium (Web Audio) playback lives at MODULE level, not per-component, so a
// started replay keeps playing after you navigate away, and only ONE premium
// stream can exist app-wide (any screen's stop/start controls the same one — no
// overlapping audio if you come back mid-play). (Randy, Jun 2026)
const _sharedCtxRef = { current: null };
const _sharedSourceRef = { current: null };



export function useVoiceQA({ slug, ask, getVoiceId, getQuestionVoiceId, onGuardBlocked, onSelfNoFunding, voiceReady, onScrollTick }) {
  // idle | listening | thinking | speaking
  const [state, setState] = useState("idle");
  const stateRef = useRef("idle");
  const setVoiceState = useCallback((s) => { stateRef.current = s; setState(s); }, []);
  // Which control "owns" the current playback (e.g. "all:device", "seg:<id>:self").
  // A control re-press = pause/resume; a DIFFERENT control starts fresh instead
  // of resuming the wrong clip. (Randy, Jun 2026)
  const playKeyRef = useRef(null);
  const [playKey, setPlayKeyState] = useState(null);
  const setActiveKey = useCallback((k) => { playKeyRef.current = k ?? null; setPlayKeyState(k ?? null); }, []);
  // Jun 2026 (Randy) — "warming" = the spin-up gap between pressing play and the
  // FIRST audio actually sounding (a self-pay cold start can be 10-15s). Drives a
  // mild shimmer on every playback button so the wait reads as intentional. It
  // flips false the instant real audio plays and never re-triggers mid-thread.
  const [warming, setWarmingState] = useState(false);
  const warmingRef = useRef(false);
  const setWarming = useCallback((w) => { warmingRef.current = w; setWarmingState(w); }, []);
  const audioStartedRef = useRef(false);
  // Follow-along scroll hook: (messageIndex, fromFrac, toFrac, durationMs).
  // Kept in a ref so the long-lived speak loops always see the latest closure.
  const onScrollTickRef = useRef(onScrollTick);
  useEffect(() => { onScrollTickRef.current = onScrollTick; }, [onScrollTick]);
  // Jun 2026 (Randy) — the QUESTION voice for the two-voice whole-conversation
  // playback. Kept in a ref so processBlob (a long-lived callback) can warm the
  // spoken question into the cache in the SAME voice the Listen pill expects.
  const getQuestionVoiceIdRef = useRef(getQuestionVoiceId);
  useEffect(() => { getQuestionVoiceIdRef.current = getQuestionVoiceId; }, [getQuestionVoiceId]);

  const streamRef = useRef(null);
  const audioCtxRef = _sharedCtxRef; // shared singleton (survives unmount)
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const sourceNodeRef = _sharedSourceRef; // shared singleton (survives unmount)
  const activeRef = useRef(false); // conversation on/off
  const mimeRef = useRef("audio/webm");
  const sessionIdRef = useRef(null);
  // VAD bookkeeping
  const speakingStartedRef = useRef(0);
  const lastVoiceRef = useRef(0);
  const capturingRef = useRef(false);
  const cancelSpeakRef = useRef(false);
  const devicePumpRef = useRef(null); // keep-alive timer for the free device voice
  // Jun 2026 (Randy, Option C) — whether the silo's PREMIUM (OpenAI) voice is
  // funded/ready right now. When false (or if a premium call fails), the Listen
  // features fall back to the device's free built-in voice. Kept in a ref so the
  // long-lived speak loops always read the latest value.
  const voiceReadyRef = useRef(!!voiceReady);
  useEffect(() => { voiceReadyRef.current = !!voiceReady; }, [voiceReady]);

  // Warm up the browser's voice list early so the FREE voice has a voice ready
  // the first time it's tapped (Safari populates this lazily).
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    _refreshVoices();
    try { window.speechSynthesis.onvoiceschanged = _refreshVoices; } catch { /* */ }
  }, []);

  const supported =
    typeof window !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof window.MediaRecorder !== "undefined";

  // The phone/browser's built-in voice (Web Speech API). Free, no key, no
  // server, no storage — our always-available fallback for the Listen features.
  const deviceVoiceSupported =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  const handleVoiceError = useCallback(
    (e) => {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      const code = data.guard_code || (data.detail && typeof data.detail === "object" ? data.detail.code : null);
      // "Pay for this myself" lane with no personal funding → route to the
      // add-key / fund-voucher setup CTA instead of the owner money-guard.
      if (status === 402 && code === "SELF_VOICE_NO_FUNDING") {
        onSelfNoFunding?.(data.detail);
        return true;
      }
      if (status === 402 && GUARD_CODES.includes(code)) {
        onGuardBlocked?.(data.detail);
        return true; // handled — caller should end the conversation
      }
      // Jun 2026 (Randy) — cold-start grace. The first tap after a quiet spell
      // can be slow while the speech pipeline warms up; we time out at 50s (just
      // under the 60s proxy cutoff) so the visitor gets a friendly "tap again"
      // instead of a raw 502/timeout — and the conversation stays alive.
      const timedOut = e?.code === "ECONNABORTED" || [502, 503, 504, 408].includes(status);
      if (timedOut) {
        toast("Voice is warming up — give it one more tap. 🎙️", { duration: 3500 });
        return false; // keep the conversation; caller drops back to listening
      }
      const detail = data.detail;
      toast.error(typeof detail === "string" ? detail : "Voice didn't work — please try again.");
      return false;
    },
    [onGuardBlocked, onSelfNoFunding],
  );

  const pickMime = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
    for (const c of candidates) {
      if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  // One shared AudioContext, created/resumed inside a user gesture (the mic tap
  // or a replay tap) so iOS lets us play audio programmatically.
  const ensureCtx = (sessionType = "playback") => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    // Jun 2026 (Randy) — ask the OS to treat our premium voice as "playback" so it
    // plays even when the iPhone's physical mute/ringer switch is OFF (Apple
    // silences only "ambient" web audio with that switch, never "playback"). This
    // is the single gateway for ALL premium audio, so it applies everywhere voice
    // can play. Harmless no-op where the AudioSession API isn't supported.
    // Jul 2026 (Randy) — the mic path passes "play-and-record" so that starting a
    // voice conversation right AFTER a replay (which left the session on
    // "playback") re-opens the mic instead of freezing.
    // Sep 2026 (Randy — first-words clipped, again) — only WRITE this when it
    // actually changes. Assigning it on every speak call (once per turn) makes
    // iOS re-configure the audio route mid-conversation, and a route
    // re-configure swallows roughly the first second of the buffer — which is
    // exactly "the voice actor misses the first 3-4 words" on every question
    // AND every answer.
    try {
      if (typeof navigator !== "undefined" && navigator.audioSession) {
        if (navigator.audioSession.type !== sessionType) navigator.audioSession.type = sessionType;
      }
    } catch { /* */ }
    return audioCtxRef.current;
  };

  // Sep 29 2026 (Randy — Windows clipping, UNDO-FIRST). The Sep wake-dither,
  // the 200ms keep-alive pump and the 800ms lead were built on a sleeping-
  // speaker theory his own testing disproved: desktop Chrome AND Edge clip on
  // EVERY play (not just the first), the same iPhone never clips, and the
  // bytes plus Chrome's own graph output are provably complete. All of it is
  // gone. What's left is ONE route-hold source, and every value is a dial in
  // lib/voicePlayback.js that the Voice Lead-In Lab can move — the defaults
  // reproduce the pre-Sep behaviour exactly.
  const routeHoldRef = useRef(null);
  const stopRouteHold = useCallback(() => {
    if (routeHoldRef.current) {
      try { routeHoldRef.current(); } catch { /* */ }
      routeHoldRef.current = null;
    }
  }, []);
  const holdRoute = useCallback((ctx) => {
    stopRouteHold();
    routeHoldRef.current = startRouteHold(ctx, getVoiceTuning());
  }, [stopRouteHold]);

  const stopPlayback = useCallback(() => {
    cancelSpeakRef.current = true;
    stopRouteHold();
    setWarming(false);
    audioStartedRef.current = false;
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.onended = null; sourceNodeRef.current.stop(); } catch { /* */ }
      sourceNodeRef.current = null;
    }
    if (devicePumpRef.current) { clearInterval(devicePumpRef.current); devicePumpRef.current = null; }
    // Also silence the free device voice if it's mid-sentence.
    try { if (deviceVoiceSupported) window.speechSynthesis.cancel(); } catch { /* */ }
  }, [deviceVoiceSupported, stopRouteHold, setWarming]);

  // Jun 2026 → Sep 2026 (Randy — overlapping-voice fix). RUN TOKEN. `stopPlayback`
  // flips cancelSpeakRef, but every speak entry point re-arms it to false, so a
  // superseded loop parked in an `await` woke up, saw "not cancelled", and kept
  // feeding audio ON TOP of the new playback. Each run now carries its own id: a
  // loop that is no longer the current run bails instead of playing.
  const runIdRef = useRef(0);
  const isStale = useCallback((run) => run !== undefined && run !== runIdRef.current, []);
  const beginRun = useCallback(() => {
    stopPlayback();
    cancelSpeakRef.current = false;
    runIdRef.current += 1;
    return runIdRef.current;
  }, [stopPlayback]);

  const teardownAudioGraph = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch { /* */ }
    recorderRef.current = null;
    capturingRef.current = false;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    analyserRef.current = null;
    // Keep audioCtxRef alive for replay reuse; closed on unmount.
  }, []);

  const stopConversation = useCallback(() => {
    activeRef.current = false;
    sessionIdRef.current = null;
    stopPlayback();
    teardownAudioGraph();
    setVoiceState("idle");
  }, [stopPlayback, teardownAudioGraph, setVoiceState]);

  // base64 mp3 → ArrayBuffer (for Web Audio decode).
  const b64ToBuffer = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  };

  const fetchChunkBuffer = useCallback(
    async (text, sessionId, voiceOverride, payer, cacheOnly) => {
      const voice = voiceOverride || (getVoiceId && getVoiceId()) || undefined;
      const sp = await api.post(`/s/${slug}/voice/speak`, {
        text, voice, voice_session_id: sessionId,
        allow_voice_override: !!voiceOverride,
        payer: payer || "sponsored",
        cache_only: !!cacheOnly,
      }, { timeout: 50000 });
      const b64 = sp.data?.audio_base64;
      return b64 ? b64ToBuffer(b64) : null;
    },
    [slug, getVoiceId],
  );

  // `withLead` = this is the FIRST chunk of a playback, so it gets the lead-in
  // (length + level + schedule-ahead all come from the tuning dials). onStart
  // fires when the first WORD sounds, not when the lead-in starts, so the
  // follow-along scroll and the scrubber playhead track the audio.
  const playBuffer = useCallback(
    (ctx, arrayBuffer, onStart, withLead = false, run) =>
      new Promise((resolve) => {
        if (cancelSpeakRef.current || isStale(run) || !arrayBuffer) return resolve();
        ctx.decodeAudioData(
          arrayBuffer,
          (audioBuf) => {
            if (cancelSpeakRef.current || isStale(run)) return resolve();
            const { src, startAt, audibleDelayMs } = buildChunkSource(ctx, audioBuf, getVoiceTuning(), { withLead });
            src.connect(ctx.destination);
            sourceNodeRef.current = src;
            src.onended = () => { if (sourceNodeRef.current === src) sourceNodeRef.current = null; resolve(); };
            try {
              src.start(startAt);
              if (onStart) {
                if (audibleDelayMs > 0) {
                  setTimeout(() => {
                    if (!cancelSpeakRef.current && !isStale(run)) onStart(audioBuf.duration);
                  }, audibleDelayMs);
                } else {
                  onStart(audioBuf.duration);
                }
              }
            } catch { resolve(); }
          },
          () => resolve(),
        );
      }),
    [isStale],
  );

  // Speak an answer FAST and GAPLESS. We validate the FIRST chunk (so an
  // auth/funding error surfaces immediately), then fire EVERY remaining chunk's
  // TTS fetch CONCURRENTLY and play them in order. Firing them in parallel means
  // a single slow/rate-limited request (OpenAI can back off ~25-30s on a 429)
  // downloads alongside the others while earlier chunks play — so it no longer
  // creates dead air in the middle of an answer. (Jun 2026, Randy — mid-answer
  // silence fix.)
  const speakPipelined = useCallback(
    async (answer, sessionId, voiceOverride, { silentFail = false, payer, cacheOnly = false, onProgress, run } = {}) => {
      const chunks = chunkForSpeech(answer);
      if (!chunks.length) return true;
      cancelSpeakRef.current = false;
      if (!audioStartedRef.current) setWarming(true); // spin-up shimmer until first audio
      const ctx = ensureCtx();
      try { await ctx.resume(); } catch { /* */ }
      // Warm up the output path with a sliver of silence so the context is fully
      // running before the first word — fixes the "first 3-4 words clipped" bug.
      try {
        const warm = ctx.createBufferSource();
        warm.buffer = ctx.createBuffer(1, Math.max(1, Math.ceil(ctx.sampleRate * 0.14)), ctx.sampleRate);
        warm.connect(ctx.destination);
        warm.start();
      } catch { /* */ }
      // ...and KEEP it awake through the whole first synth (self-pay cold starts
      // can take 10-15s, long enough for the speaker to re-sleep). Stopped the
      // instant the first audio is in hand, below.
      holdRoute(ctx);
      // Char offsets so the follow-along scroll can map each chunk to its slice
      // of the message's text (start→end fraction).
      const offsets = [];
      let acc = 0;
      for (const c of chunks) { offsets.push(acc); acc += c.length; }
      const total = acc || 1;
      const fracStart = (i) => offsets[i] / total;
      const fracEnd = (i) => (offsets[i] + chunks[i].length) / total;
      const tick = (i) => (durSec) => onProgress?.(fracStart(i), fracEnd(i), (durSec || 0) * 1000);
      // 1) First chunk alone — fast start + a clean place to surface a key/
      //    funding error before we fan out the rest. Jun 2026 (Randy) — the very
      //    first synth after an idle spell can cold-start slow or 502; auto-retry
      //    ONCE (the first attempt usually warmed the 24h cache) so the user
      //    doesn't have to manually tap again.
      let firstAb = null;
      try { firstAb = await fetchChunkBuffer(chunks[0], sessionId, voiceOverride, payer, cacheOnly); }
      catch (e) {
        const st = e?.response?.status;
        const transient = e?.code === "ECONNABORTED" || [502, 503, 504, 408, 429].includes(st);
        if (transient && !cancelSpeakRef.current && !isStale(run)) {
          await new Promise((r) => setTimeout(r, 1200));
          try { firstAb = await fetchChunkBuffer(chunks[0], sessionId, voiceOverride, payer, cacheOnly); }
          catch (e2) { stopRouteHold(); setWarming(false); if (silentFail) return false; handleVoiceError(e2); return false; }
        } else {
          stopRouteHold();
          setWarming(false);
          if (silentFail) return false; // nothing played → caller can fall back
          handleVoiceError(e);
          return false;
        }
      }
      // Keep the KEEP-ALIVE running until the FIRST real audio actually starts
      // (stopped inside playBuffer's onStart below). Stopping it here left a gap —
      // decode + device wake — where the speaker could re-sleep and swallow the
      // opening words. (Randy — first-words clip, hardened.)
      // 2) Fan out ALL remaining chunks at once (parallel downloads).
      const rest = chunks.slice(1).map((c) =>
        fetchChunkBuffer(c, sessionId, voiceOverride, payer, cacheOnly).catch(() => null),
      );
      if (cancelSpeakRef.current || isStale(run)) { stopRouteHold(); setWarming(false); return true; }
      audioStartedRef.current = true; setWarming(false); // first audio is sounding — drop the shimmer
      // The route hold runs from the tap until the first WORD actually sounds
      // (playBuffer fires onStart at the audible start, not at the lead-in's),
      // then stops. Lead length, hold level and schedule-ahead are dials.
      await playBuffer(ctx, firstAb, (d) => { stopRouteHold(); tick(0)(d); }, true, run);
      stopRouteHold(); // safety: also stop if the first chunk never decoded (onStart never fired)
      let played = true;
      for (let i = 0; i < rest.length; i++) {
        if (cancelSpeakRef.current || isStale(run)) break;
        const ab = await rest[i];
        await playBuffer(ctx, ab, tick(i + 1), false, run); // null → resolves instantly (skip), never stalls
      }
      return played;
    },
    [fetchChunkBuffer, playBuffer, handleVoiceError, holdRoute, stopRouteHold, setWarming, isStale],
  );

  // Speak text with the FREE device voice (Web Speech API), sentence-by-sentence
  // so Chrome's ~15s utterance cutoff can't truncate it. `pitch` lets us tell
  // the asker apart from the silo's answer in a whole-conversation playback.
  const speakViaDeviceChunk = useCallback(
    (text, { pitch = 1, rate = 1 } = {}) =>
      new Promise((resolve) => {
        const clean = speechClean(text);
        if (cancelSpeakRef.current || !deviceVoiceSupported || !clean) return resolve();
        try {
          const u = new window.SpeechSynthesisUtterance(clean);
          u.pitch = pitch;
          u.rate = rate;
          let done = false;
          const finish = () => { if (!done) { done = true; resolve(); } };
          u.onend = finish;
          u.onerror = finish;
          // Safety net: some browsers never fire onend if no voices are loaded
          // yet — resolve after a generous estimate so playback can't get stuck.
          const guardMs = Math.min(60000, clean.length * 120 + 3000);
          setTimeout(finish, guardMs);
          try { window.speechSynthesis.resume(); } catch { /* */ }
          window.speechSynthesis.speak(u);
        } catch { resolve(); }
      }),
    [deviceVoiceSupported],
  );

  const speakViaDeviceText = useCallback(
    async (text, opts, onProgress, run) => {
      if (!deviceVoiceSupported) return;
      // Prime speech synthesis so Chrome doesn't clip the first utterance.
      try { window.speechSynthesis.cancel(); window.speechSynthesis.resume(); } catch { /* */ }
      const parts = deviceChunks(text);
      const offsets = [];
      let acc = 0;
      for (const c of parts) { offsets.push(acc); acc += c.length; }
      const total = acc || 1;
      for (let i = 0; i < parts.length; i++) {
        if (cancelSpeakRef.current || isStale(run)) break;
        const c = parts[i];
        // Device voice gives no real duration — estimate from length (~60ms/char).
        const durMs = Math.min(15000, Math.max(1500, c.length * 60));
        onProgress?.(offsets[i] / total, (offsets[i] + c.length) / total, durMs);
        await speakViaDeviceChunk(c, opts);
      }
    },
    [deviceVoiceSupported, speakViaDeviceChunk, isStale],
  );

  // Process one captured utterance: transcribe → grounded answer → speak.
  const processBlob = useCallback(
    async (blob) => {
      if (!activeRef.current) return;
      if (!blob || !blob.size) { if (activeRef.current) setVoiceState("listening"); return; }
      setVoiceState("thinking");
      const sid = sessionIdRef.current;
      try {
        const mime = mimeRef.current || "audio/webm";
        const ext = mime && (mime.includes("mp4") || mime.includes("aac")) ? "m4a" : "webm";
        const fd = new FormData();
        fd.append("audio", blob, `question.${ext}`);
        if (sid) fd.append("voice_session_id", sid);
        const tr = await api.post(`/s/${slug}/voice/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 50000 });
        const text = (tr.data?.text || "").trim();
        if (!activeRef.current) return;
        if (!text) { setVoiceState("listening"); return; }
        const answer = await ask(text);
        if (!activeRef.current) return;
        if (!answer) { setVoiceState("listening"); return; }
        setVoiceState("speaking");
        // Jun 2026 (Randy) — "talking voice not saved" fix. The visitor SPOKE
        // this question (no TTS was made for it), but the whole-conversation
        // Listen pill + Share read the thread in TWO voices (question in the
        // questionVoice). Without the question's audio, every voiced turn still
        // shows as a missing/charged part. So we warm the question into the same
        // 24h cache, CONCURRENTLY with reading the answer (sponsored = owner's
        // dime, same session id → one usage line), so by the time the answer
        // finishes the whole turn is genuinely cached → the pill greens and a
        // share includes it free. A failed warm just leaves that part to fill
        // later (graceful; e.g. a silo with no voice funding).
        const qVoice = getQuestionVoiceIdRef.current && getQuestionVoiceIdRef.current();
        const qWarm = (qVoice && text)
          ? (async () => {
              for (const c of chunkForSpeech(text)) {
                try { await fetchChunkBuffer(c, sid, qVoice, "sponsored", false); } catch { /* */ }
              }
            })()
          : null;
        // Live speech-to-speech follows the newest answer bubble ("LIVE").
        await speakPipelined(answer, sid, undefined, {
          onProgress: (f0, f1, d) => onScrollTickRef.current?.("LIVE", f0, f1, d),
        });
        if (qWarm) { try { await qWarm; } catch { /* */ } }
      } catch (e) {
        const ended = handleVoiceError(e);
        if (ended) { stopConversation(); return; }
      } finally {
        if (activeRef.current) setVoiceState("listening");
      }
    },
    [slug, ask, speakPipelined, fetchChunkBuffer, handleVoiceError, stopConversation, setVoiceState],
  );

  const startCapture = useCallback(() => {
    if (!streamRef.current || capturingRef.current) return;
    try {
      const mime = mimeRef.current;
      const mr = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        capturingRef.current = false;
        const blob = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
        chunksRef.current = [];
        processBlob(blob);
      };
      recorderRef.current = mr;
      mr.start();
      capturingRef.current = true;
    } catch { /* mic busy — retry next frame */ }
  }, [processBlob]);

  const stopCapture = useCallback(() => {
    if (recorderRef.current && capturingRef.current) {
      try { recorderRef.current.stop(); } catch { capturingRef.current = false; }
    }
  }, []);

  // VAD loop — only acts while LISTENING. During thinking/speaking the mic is
  // ignored entirely (no barge-in), so noise / the reply audio can't trip it.
  const vadTick = useCallback(() => {
    if (!activeRef.current || !analyserRef.current) return;
    if (stateRef.current === "listening") {
      const a = analyserRef.current;
      const buf = new Uint8Array(a.fftSize);
      a.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (rms > SPEECH_RMS) {
        if (!capturingRef.current) { startCapture(); speakingStartedRef.current = now; }
        lastVoiceRef.current = now;
      } else if (capturingRef.current) {
        const spokeFor = now - speakingStartedRef.current;
        const quietFor = now - lastVoiceRef.current;
        if (spokeFor >= MIN_SPEECH_MS && quietFor >= SILENCE_MS) {
          stopCapture(); // → onstop → processBlob
        } else if (spokeFor < MIN_SPEECH_MS && quietFor >= SILENCE_MS) {
          try { recorderRef.current?.stop(); } catch { /* */ }
          chunksRef.current = [];
          capturingRef.current = false;
        }
      }
    }
    rafRef.current = requestAnimationFrame(vadTick);
  }, [startCapture, stopCapture]);

  const startConversation = useCallback(async () => {
    if (activeRef.current) return;
    // Jul 2026 (Randy) — FREEZE FIX. If the visitor just listened to a Q&A combo
    // (a passive replay), its audio graph + the "playback" audio session are
    // still in place. Tear them down and switch the session to "play-and-record"
    // BEFORE grabbing the mic, or iOS keeps the session in playback-only mode and
    // the mic/VAD reads silence — the voice button appears frozen.
    stopPlayback();
    try { if (typeof navigator !== "undefined" && navigator.audioSession) navigator.audioSession.type = "play-and-record"; } catch { /* */ }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      mimeRef.current = pickMime() || "audio/webm";
      const ctx = ensureCtx("play-and-record");
      try { await ctx.resume(); } catch { /* */ } // unlock iOS playback on this gesture
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      activeRef.current = true;
      // Bump the run token as well as re-arming the flag: a replay loop parked
      // in an await must not come back to life once the mic is live.
      runIdRef.current += 1;
      cancelSpeakRef.current = false;
      sessionIdRef.current = genId();
      speakingStartedRef.current = 0;
      lastVoiceRef.current = 0;
      setVoiceState("listening");
      rafRef.current = requestAnimationFrame(vadTick);
    } catch {
      toast.error("Microphone access was blocked. Allow mic access to talk.");
      stopConversation();
    }
  }, [vadTick, stopConversation, stopPlayback, setVoiceState]);

  // Mic button. idle → start. While SPEAKING → "take over" (stop the reply and
  // listen for your next question). While listening/thinking → end the chat.
  const toggle = useCallback(() => {
    if (!activeRef.current) {
      // Jul 2026 (Randy) — a passive replay (per-bubble green speaker / whole-
      // conversation) may still be playing (state ≠ idle, but no live mic
      // conversation). Kill it first so its audio graph can't collide with the
      // mic we're about to open, then start the conversation cleanly.
      if (stateRef.current !== "idle") { beginRun(); setActiveKey(null); setVoiceState("idle"); }
      startConversation();
      return;
    }
    if (stateRef.current === "speaking") {
      beginRun();
      speakingStartedRef.current = 0;
      lastVoiceRef.current = 0;
      setVoiceState("listening");
    } else {
      stopConversation();
    }
  }, [startConversation, stopConversation, beginRun, setActiveKey, setVoiceState]);

  const stopSpeaking = useCallback(() => {
    stopPlayback();
    setActiveKey(null);
    if (activeRef.current) setVoiceState("listening");
    else setVoiceState("idle");
  }, [stopPlayback, setVoiceState, setActiveKey]);

  // Jun 2026 (Randy) — PAUSE/RESUME (never restart). Suspends the shared Web
  // Audio context (premium OpenAI voice) AND the device speech engine in place;
  // tapping again resumes from exactly where it left off, so a long premium
  // generation can be paused without losing your place.
  const pauseOrResume = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (stateRef.current === "speaking") {
      try { if (ctx && ctx.state === "running") ctx.suspend(); } catch { /* */ }
      try { if (deviceVoiceSupported) window.speechSynthesis.pause(); } catch { /* */ }
      setVoiceState("paused");
    } else if (stateRef.current === "paused") {
      try { if (ctx && ctx.state === "suspended") ctx.resume(); } catch { /* */ }
      try { if (deviceVoiceSupported) window.speechSynthesis.resume(); } catch { /* */ }
      setVoiceState("speaking");
    }
  }, [deviceVoiceSupported, setVoiceState]);

  // Replay an arbitrary answer aloud (the bubble speaker icon). Standalone (not
  // part of the live conversation). Gets its own session id → one usage line.
  const speakText = useCallback(
    async (text, messageIndex, key) => {
      const t = (text || "").trim();
      if (!t || activeRef.current) return; // don't fight the live conversation
      const run = beginRun();
      setVoiceState("speaking");
      setActiveKey(key);
      const prog = (f0, f1, d) => onScrollTickRef.current?.(messageIndex, f0, f1, d);
      try {
        let ok = false;
        if (voiceReadyRef.current) {
          ok = await speakPipelined(t, genId(), undefined, { silentFail: true, onProgress: prog, run });
        }
        // Premium voice not funded / unavailable → free device voice. (Option C)
        if (!ok && !cancelSpeakRef.current && !isStale(run)) await speakViaDeviceText(t, undefined, prog, run);
      } finally {
        if (!activeRef.current && !isStale(run)) setVoiceState("idle");
      }
    },
    [speakPipelined, speakViaDeviceText, beginRun, isStale, setVoiceState, setActiveKey],
  );

  // Jun 2026 (Randy) — FREE device-voice playback that actually works on iOS.
  // iOS Safari blocks speechSynthesis.speak() unless it runs SYNCHRONOUSLY
  // inside the tap, so this is NOT async: it builds every chunk and QUEUES them
  // all in one gesture (speechSynthesis plays a queue back-to-back). Works for a
  // single segment AND a long whole conversation. `segments`: [{text,pitch,idx}].
  const speakDevice = useCallback(
    (segments, { onProgress, key } = {}) => {
      if (!deviceVoiceSupported) return false;
      const list = (segments || []).filter((s) => speechClean(s.text));
      if (!list.length) return false;
      // Kill ANY current playback first — including a premium (Web Audio) clip
      // that may still be alive/suspended — so the two voices can NEVER overlap.
      // (Randy, Jun 2026) beginRun() stops the source, cancels speech, clears the
      // keep-alive pump AND invalidates any premium loop still parked in an await.
      const run = beginRun();
      const synth = window.speechSynthesis;
      _refreshVoices();
      const v = _pickEnglishVoice();
      const utterances = [];
      list.forEach((seg) => {
        const parts = deviceChunks(seg.text);
        const offs = [];
        let acc = 0;
        for (const p of parts) { offs.push(acc); acc += p.length; }
        const total = acc || 1;
        parts.forEach((p, i) => {
          const u = new window.SpeechSynthesisUtterance(p);
          u.lang = "en-US";
          if (v) u.voice = v;
          u.pitch = seg.pitch ?? 1;
          const f0 = offs[i] / total;
          const f1 = (offs[i] + p.length) / total;
          const durMs = Math.min(15000, Math.max(1500, p.length * 60));
          u.onstart = () => { if (!cancelSpeakRef.current && !isStale(run)) onProgress?.(seg.idx, f0, f1, durMs); };
          utterances.push(u);
        });
      });
      if (!utterances.length) return false;
      utterances[utterances.length - 1].onend = () => {
        if (devicePumpRef.current) { clearInterval(devicePumpRef.current); devicePumpRef.current = null; }
        if (!activeRef.current) setVoiceState("idle");
      };
      setVoiceState("speaking");
      setActiveKey(key);
      // Queue everything synchronously so the FIRST speak fires inside the tap.
      for (const u of utterances) { try { synth.speak(u); } catch { /* */ } }
      // Chrome silently pauses a long queue after ~15s; nudge it back to life,
      // but never override a deliberate user pause.
      devicePumpRef.current = setInterval(() => {
        try {
          if (cancelSpeakRef.current || stateRef.current === "paused") return;
          if (synth.paused && synth.speaking) synth.resume();
          if (!synth.speaking && !synth.pending) {
            clearInterval(devicePumpRef.current);
            devicePumpRef.current = null;
          }
        } catch { /* */ }
      }, 3000);
      return true;
    },
    [deviceVoiceSupported, setVoiceState, setActiveKey, beginRun, isStale],
  );

  // Jun 2026 (Randy, Part 2) — smart cache probe. Asks the backend (cost-FREE,
  // cache_only) whether a (question+answer) segment is STILL saved in the 24h
  // cache. Checks the FIRST speech chunk — if it's there the user grabbed this
  // segment; if it's gone the clip was either never played OR aged past its 24h
  // TTL. Used by "Push Cached Convo" to detect listened / skipped / expired parts.
  const probeCached = useCallback(
    async (text, voiceOverride) => {
      const chunks = chunkForSpeech(text || "");
      if (!chunks.length) return false;
      try {
        const voice = voiceOverride || (getVoiceId && getVoiceId()) || undefined;
        const sp = await api.post(
          `/s/${slug}/voice/speak`,
          { text: chunks[0], voice, allow_voice_override: !!voiceOverride, payer: "self", cache_only: true },
          { timeout: 20000 },
        );
        return !!(sp.data && sp.data.audio_base64);
      } catch {
        return false;
      }
    },
    [slug, getVoiceId],
  );

  // Jun 2026 (Randy, Part 2) — pre-generate (and 24h-cache) a segment WITHOUT
  // playing it, chunked EXACTLY as playback reads it so the gap-fill audio lands
  // under the same cache keys the stitched replay looks up. Billed once.
  const warmCached = useCallback(
    async (text, voiceOverride) => {
      const chunks = chunkForSpeech(text || "");
      const sid = genId();
      for (const c of chunks) {
        try { await fetchChunkBuffer(c, sid, voiceOverride, "self", false); }
        catch { /* a single failed chunk just stays a gap */ }
      }
    },
    [fetchChunkBuffer],
  );

  // Jun 2026 (Randy) — AI Assistant per-answer listen now reads the PRIOR
  // QUESTION first, then flows straight into the ANSWER as ONE continuous clip
  // in a SINGLE voice. Caching the (question+answer) pair this way is the
  // groundwork that lets "Push Cached Convo" later stitch the whole thread for
  // free (each cached segment = question + answer packaged together).
  const speakSegment = useCallback(
    async (question, answer, { voice, mode = "self", idx, key } = {}) => {
      if (activeRef.current) return false; // don't fight the live mic conversation
      const q = (question || "").trim();
      const a = (answer || "").trim();
      if (!a) return false;
      const combined = q ? `${q}\n\n${a}` : a;
      const run = beginRun();
      setVoiceState("speaking");
      setActiveKey(key);
      const prog = (f0, f1, d) => onScrollTickRef.current?.(idx ?? "buddy", f0, f1, d);
      try {
        if (mode === "device") {
          await speakViaDeviceText(combined, undefined, prog, run);
          return true;
        }
        if (mode === "self") {
          // Owner pays on their own key — no free fallback (its setup CTA shows
          // on no-funding via handleVoiceError).
          return await speakPipelined(combined, genId(), voice || undefined, { payer: "self", onProgress: prog, run });
        }
        // sponsored → premium then free device fallback
        let ok = false;
        if (voiceReadyRef.current) {
          ok = await speakPipelined(combined, genId(), voice || undefined, { silentFail: true, payer: "sponsored", onProgress: prog, run });
        }
        if (!ok && !cancelSpeakRef.current && !isStale(run)) await speakViaDeviceText(combined, undefined, prog, run);
        return true;
      } finally {
        if (!activeRef.current && !isStale(run)) setVoiceState("idle");
      }
    },
    [speakPipelined, speakViaDeviceText, beginRun, isStale, setVoiceState, setActiveKey],
  );

  // Jun 2026 (Randy, Part 2) — "Push Cached Convo" FREE stitched replay. Plays
  // the already-paid (question+answer) segments in order, gapless, each in the
  // voice it was cached with. cacheOnly → the backend ONLY returns 24h-cached
  // audio (never synthesizes/bills); any not-yet-cached segment is a gap and is
  // simply skipped here (the gap-fill popup handles filling those).
  const speakCached = useCallback(
    async (segments, { key } = {}) => {
      if (activeRef.current) return false;
      const list = (segments || []).filter((s) => (s.text || "").trim());
      if (!list.length) return false;
      const run = beginRun();
      setVoiceState("speaking");
      setActiveKey(key);
      try {
        for (let i = 0; i < list.length; i++) {
          if (cancelSpeakRef.current || isStale(run)) break;
          const seg = list[i];
          const prog = (f0, f1, d) => onScrollTickRef.current?.(seg.idx ?? i, f0, f1, d);
          await speakPipelined(seg.text, genId(), seg.voice || undefined, {
            payer: "self", cacheOnly: true, silentFail: true, onProgress: prog, run,
          });
        }
        return true;
      } finally {
        if (!activeRef.current && !isStale(run)) setVoiceState("idle");
      }
    },
    [speakPipelined, beginRun, isStale, setVoiceState, setActiveKey],
  );

  // Jun 2026 — "Listen to this whole conversation." Reads every turn top→bottom.
  // Premium (OpenAI) voices when the silo is funded — answers in `answerVoice`,
  // questions in `questionVoice`; all chunks share ONE session id so the owner
  // is billed a single aggregated voice line. When premium isn't available it
  // falls back to the FREE device voice (Option C), telling the asker (higher
  // pitch) apart from the silo's answer (lower pitch).
  const speakConversation = useCallback(
    async (turns, { answerVoice, questionVoice, mode = "sponsored", key } = {}) => {
      if (activeRef.current) return; // don't fight the live mic conversation
      const list = (turns || []).filter((t) => (t.text || "").trim());
      if (!list.length) return;
      const run = beginRun();
      const sid = genId();
      setVoiceState("speaking");
      setActiveKey(key);
      try {
        for (let ti = 0; ti < list.length; ti++) {
          const turn = list[ti];
          if (cancelSpeakRef.current || isStale(run)) break;
          const isQuestion = turn.role === "user";
          const txt = (turn.text || "").trim();
          const pitch = isQuestion ? { pitch: 1.18 } : { pitch: 0.92 };
          // Follow-along: scroll within THIS turn's message as it's read.
          const prog = (f0, f1, d) => onScrollTickRef.current?.(turn.idx, f0, f1, d);
          if (mode === "device") {
            // FREE lane — always the device's built-in voice.
            await speakViaDeviceText(txt, pitch, prog, run);
            continue;
          }
          const v = isQuestion ? questionVoice : answerVoice;
          if (mode === "self") {
            // "Pay for this myself" — listener's own key/voucher. No free
            // fallback; handleVoiceError surfaces the setup CTA on no-funding.
            const ok = await speakPipelined(txt, sid, v || undefined, { payer: "self", onProgress: prog, run });
            if (!ok) break;
            continue;
          }
          // "sponsored" — creator's dime (premium), falling back to FREE device.
          let ok = false;
          if (voiceReadyRef.current) {
            ok = await speakPipelined(txt, sid, v || undefined, { silentFail: true, payer: "sponsored", onProgress: prog, run });
          }
          if (!ok && !cancelSpeakRef.current && !isStale(run)) await speakViaDeviceText(txt, pitch, prog, run);
        }
      } finally {
        if (!activeRef.current && !isStale(run)) setVoiceState("idle");
      }
    },
    [speakPipelined, speakViaDeviceText, beginRun, isStale, setVoiceState, setActiveKey],
  );

  // Jun 2026 (Randy) — FREE playback of a shared/loaded bookmark's ALREADY-PAID
  // audio. Takes a list of base64 mp3 chunks (served by /shared-bookmark/{id}/audio
  // or /bookmarks/{id}/audio) and plays them in order, gapless, through the shared
  // Web Audio context. PURE playback: no TTS calls, no key, no charge — the sender
  // (or the owner) already paid for it. Pause/resume works via pauseOrResume().
  const playPrefetched = useCallback(
    async (b64Chunks, { key } = {}) => {
      const list = (b64Chunks || []).filter(Boolean);
      if (!list.length) return false;
      if (activeRef.current) return false; // don't fight the live mic conversation
      const run = beginRun();
      const ctx = ensureCtx();
      try { await ctx.resume(); } catch { /* */ }
      setVoiceState("speaking");
      setActiveKey(key);
      try {
        for (let i = 0; i < list.length; i++) {
          if (cancelSpeakRef.current || isStale(run)) break;
          let ab = null;
          try { ab = b64ToBuffer(list[i]); } catch { ab = null; }
          await playBuffer(ctx, ab, undefined, i === 0, run);
        }
        return true;
      } finally {
        if (!activeRef.current && !isStale(run)) setVoiceState("idle");
      }
    },
    [beginRun, isStale, playBuffer, setVoiceState, setActiveKey],
  );

  useEffect(
    () => () => {
      // A LIVE mic conversation stops fully on leave. But a standalone replay
      // (premium or free) is allowed to keep playing while the user navigates
      // elsewhere in the app — only the mic graph is released. The shared audio
      // context is kept alive (not closed) so the replay continues. (Randy)
      if (activeRef.current) {
        activeRef.current = false;
        stopPlayback();
      }
      teardownAudioGraph();
    },
    [stopPlayback, teardownAudioGraph],
  );

  return { supported, state, warming, playKey, toggle, stopSpeaking, pauseOrResume, speakText, speakSegment, speakDevice, speakCached, speakConversation, probeCached, warmCached, playPrefetched };
}
